import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { EditableText } from '@/core/EditableText.atom';
import { IconSelect } from '@/core/IconSelect.molecule';
import { FormulaEditorAsync } from '@/formula/FormulaEditorAsync.molecule';
import { OutputItemMetadataLabel } from '@/tools/models/OutputItemMetadataLabel.atom';
import { OUTPUT_SECTION_BORDER_STYLES } from '@/tools/models/template.types';
import { Badge, Icon } from '@seeqdev/qomponents';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';

interface FormulaErrors {
  message: string;
  column: number;
  line: number;
}

interface FormulaWithParameters {
  formula: string;
  parameters: { name: string; identifier: string; item?: { id?: string } }[];
  formulaErrors?: FormulaErrors[];
}

export interface FormulaItemTableOption {
  key: string;
  display: string;
  allFormulas?: FormulaWithParameters[];
  id?: string;
  isNew?: boolean;
  showNewLabel?: boolean;
  isMapped?: boolean;
  mappedFeatureId?: string;
  /** Wire-format item type from the source derived feature (e.g. "Signal", "Scalar", "Condition"). */
  outputItemType?: string;
  /**
   * Wire-format metadata map from the source derived feature. `undefined` means the feature has no
   * restriction; the metadata annotation renders "Any" in that state.
   */
  outputItemMetadata?: Record<string, string>;
}

export interface FormulaItemTableIF {
  options: FormulaItemTableOption[];
  testId?: string;
  extraClassNames?: string;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: string;
  additionalIconTooltip?: string;
  onSecondaryIconClick?: (key: string) => void;
  secondaryIcon?: string;
  secondaryIconTooltip?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  hasErrors?: boolean;
  newLabel?: string;
  mappingConfig?: FeatureMappingConfig;
}

export interface FeatureMappingConfig {
  label: string;
  labelTooltip?: string;
  mappingPlaceholder?: string;
  mappingOptions: ReactSelectOption[];
  onMappingChange?: (optionKey: string, isMapped: boolean, mappedFeatureId?: string) => void;
}

/**
 * A row is treated as a model derived feature (and therefore opted in to the metadata annotation)
 * when the option object carries the metadata keys — even with undefined values. Model display
 * utilities always project these keys onto every option they produce; non-model callers don't, so
 * their rows render without the annotation.
 */
const isModelDerivedFeatureOption = (option: FormulaItemTableOption): boolean =>
  'outputItemType' in option || 'outputItemMetadata' in option;

export const FormulaItemTable = ({
  extraClassNames,
  options,
  testId,
  onEditClick,
  onRemoveClick,
  onAdditionalIconClick,
  additionalIcon,
  additionalIconTooltip,
  onSecondaryIconClick,
  secondaryIcon,
  secondaryIconTooltip,
  onTextChange,
  textEditable,
  hasErrors = false,
  newLabel,
  mappingConfig,
}: FormulaItemTableIF) => {
  const { t } = useTranslation();

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Expand all rows if hasErrors becomes true
  useEffect(() => {
    if (hasErrors) {
      setExpandedKeys(new Set(options.map((opt) => opt.key)));
    }
  }, [hasErrors, options]);

  const toggleRow = useCallback((option: FormulaItemTableOption) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(option.key)) {
        next.delete(option.key);
      } else {
        next.add(option.key);
      }
      return next;
    });
  }, []);

  return (
    <table
      className={clsx('table table-striped table-condensed mb0 width-100percent', extraClassNames)}
      data-testid={testId}
      tabIndex={0}>
      <tbody>
        {options.map((option) => {
          const isOpen = expandedKeys.has(option.key);
          const showNewLabel = !!newLabel && (option.isNew || option.showNewLabel);
          return (
            <tr key={option.key} className={clsx(OUTPUT_SECTION_BORDER_STYLES, 'tw:border-t')}>
              <td colSpan={1} className="forceZeroPadding">
                <div className="tw:relative tw:px-[10px] tw:py-[5px]">
                  <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                      {!textEditable && (
                        <span>
                          <span className="text-bold">{t('NAME')}:</span> {option.display}
                          {showNewLabel ? <Badge extraClassNames="tw:ml-[7px] text-bold">{newLabel}</Badge> : null}
                        </span>
                      )}
                      {textEditable && onTextChange && (
                        <span className="tw:flex tw:flex-row tw:items-center tw:gap-1">
                          <span className="text-bold">{t('NAME')}:</span>
                          <EditableText
                            value={option.display}
                            onUpdate={(newText) => onTextChange(newText, option)}
                            textClasses="tw:inline-block"
                          />
                          {showNewLabel ? <Badge extraClassNames="tw:ml-[7px] text-bold">{newLabel}</Badge> : null}
                        </span>
                      )}
                      {isModelDerivedFeatureOption(option) && (
                        <OutputItemMetadataLabel
                          outputItemType={option.outputItemType}
                          outputItemMetadata={option.outputItemMetadata}
                        />
                      )}
                    </div>
                    <div className="tw:flex tw:flex-row tw:items-center">
                      {option.allFormulas && (
                        <Icon
                          icon="fc fc-formula"
                          onClick={() => toggleRow(option)}
                          testId={`${testId}FormulaIcon-${option.key}`}
                        />
                      )}
                      {onEditClick && (
                        <Icon
                          icon="fa-pencil"
                          onClick={() => onEditClick(option.key)}
                          testId={`${testId}EditIcon-${option.key}`}
                          extraClassNames="tw:ml-[8px]"
                        />
                      )}
                      {onAdditionalIconClick && additionalIcon && (
                        <Icon
                          icon={additionalIcon}
                          onClick={() => onAdditionalIconClick(option.key)}
                          testId={`${testId}ExtraIcon-${option.key}`}
                          extraClassNames="tw:ml-[8px]"
                          tooltip={additionalIconTooltip && t(additionalIconTooltip)}
                        />
                      )}
                      {onSecondaryIconClick && secondaryIcon && (
                        <Icon
                          icon={secondaryIcon}
                          onClick={() => onSecondaryIconClick(option.key)}
                          testId={`${testId}SecondaryExtraIcon-${option.key}`}
                          extraClassNames="tw:ml-[8px]"
                          tooltip={secondaryIconTooltip && t(secondaryIconTooltip)}
                        />
                      )}
                      {onRemoveClick && (
                        <Icon
                          icon="fa-trash"
                          onClick={() => onRemoveClick(option.key)}
                          testId={`${testId}RemoveIcon-${option.key}`}
                          extraClassNames="tw:ml-[8px]"
                        />
                      )}
                    </div>
                  </div>
                  {option.isNew && mappingConfig && mappingConfig.mappingOptions.length > 0 && (
                    <div className="mb8 mt10">
                      <Checkbox
                        isChecked={option.isMapped ?? false}
                        onChange={(e) => mappingConfig.onMappingChange?.(option.key, e.target.checked, undefined)}
                        label={t('TEMPLATES.MAP_TO_EXISTING_CHECKBOX_LABEL')}
                        id={`${option.key}-mapping-checkbox`}
                      />
                    </div>
                  )}
                  {option.isNew && option.isMapped && mappingConfig && mappingConfig.mappingOptions.length > 0 && (
                    <div className="mb8">
                      {mappingConfig && mappingConfig.label && mappingConfig.labelTooltip && (
                        <div className="flexColumnContainer flexAlignCenter mb5" data-testid={`${testId}MappingLabel`}>
                          <span className={clsx('mr5')}>{t(mappingConfig?.label)}</span>
                          <Icon
                            icon="fa-question-circle"
                            large={true}
                            tooltip={t(mappingConfig?.labelTooltip)}
                            testId={`${testId}MappingLabelIcon`}
                            tooltipPlacement="right"
                            extraClassNames="tw:cursor-default"
                          />
                        </div>
                      )}
                      <IconSelect
                        testId={`${testId}MappingSelect-${option.key}`}
                        selectOptions={mappingConfig?.mappingOptions}
                        placeholder={mappingConfig?.mappingPlaceholder}
                        value={option.mappedFeatureId}
                        onChange={(selected) =>
                          mappingConfig.onMappingChange?.(option.key, true, selected.value as string)
                        }
                      />
                    </div>
                  )}
                  {isOpen && option.allFormulas && (
                    <div>
                      {option.allFormulas.map((formula, idx) => (
                        <div
                          key={idx}
                          className={clsx(
                            idx === 0 && 'tw:mt-[5px]',
                            idx !== option.allFormulas!.length - 1 && 'tw:mb-[12px]',
                          )}>
                          <span className="text-bold">
                            {t(
                              option.allFormulas!.length > 1 ? 'FORMULA.FORMULA_COUNT' : 'FORMULA.FORMULA',
                              option.allFormulas!.length > 1 ? { count: idx + 1 } : undefined,
                            )}
                            :
                          </span>{' '}
                          <FormulaEditorAsync
                            formula={formula.formula}
                            readOnly={true}
                            readOnlyWithErrors={true}
                            showLineNumbers={false}
                            lineWrapping={true}
                            formulaErrors={formula.formulaErrors}
                          />
                          {formula.parameters && formula.parameters.length > 0 && (
                            <div>
                              <span className="text-bold tw:mt-[5px]">{t('FORMULA.DOCUMENTATION.PARAMETERS')}: </span>{' '}
                              {formula.parameters.map((param) => `${param.identifier} (${param.name})`).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
